import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { createLease } from "../lib/api";
import { FileSignature } from "lucide-react";

export default function OfferLeaseDialog({ open, onOpenChange, showing, listing, onLeaseCreated }) {
  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    monthly_rent: listing?.price ? String(listing.price) : "",
    deposit: listing?.price ? String(listing.price) : "",
    lease_type: listing?.lease_type || "long",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date || !form.monthly_rent) {
      setError("Start date, end date, and monthly rent are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        listing_id: listing?.listing_id || showing?.listing_id,
        renter_id: showing?.renter_id,
        lease_type: form.lease_type,
        start_date: form.start_date,
        end_date: form.end_date,
        monthly_rent: parseFloat(form.monthly_rent),
        deposit: parseFloat(form.deposit || 0),
      };
      const res = await createLease(payload);
      if (onLeaseCreated) onLeaseCreated(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create lease. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="offer-lease-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Fraunces'] text-2xl flex items-center gap-2">
            <FileSignature className="w-6 h-6 text-[#1a6b3c]" />
            Offer a Lease
          </DialogTitle>
          <DialogDescription className="text-[#5a5a5a]">
            Draft the lease terms for <span className="font-semibold text-[#0d0d0d]">{showing?.renter_name}</span>
            {showing?.listing_title ? <> on <span className="font-semibold text-[#0d0d0d]">{showing.listing_title}</span></> : null}. They'll sign it from their dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Start Date</label>
              <Input
                data-testid="lease-start-date"
                type="date"
                value={form.start_date}
                onChange={(e) => handleChange("start_date", e.target.value)}
                className="rounded-xl border-black/[0.1] h-11"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">End Date</label>
              <Input
                data-testid="lease-end-date"
                type="date"
                value={form.end_date}
                onChange={(e) => handleChange("end_date", e.target.value)}
                className="rounded-xl border-black/[0.1] h-11"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Lease Type</label>
            <Select value={form.lease_type} onValueChange={(v) => handleChange("lease_type", v)}>
              <SelectTrigger className="rounded-xl border-black/[0.1] h-11" data-testid="lease-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="short">Short-term (under 6 months)</SelectItem>
                <SelectItem value="mid">Mid-term (6–12 months)</SelectItem>
                <SelectItem value="long">Long-term (1 year+)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Monthly Rent ($)</label>
              <Input
                data-testid="lease-monthly-rent"
                type="number"
                min="0"
                step="50"
                value={form.monthly_rent}
                onChange={(e) => handleChange("monthly_rent", e.target.value)}
                placeholder="1350"
                className="rounded-xl border-black/[0.1] h-11"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Security Deposit ($)</label>
              <Input
                data-testid="lease-deposit"
                type="number"
                min="0"
                step="50"
                value={form.deposit}
                onChange={(e) => handleChange("deposit", e.target.value)}
                placeholder="1350"
                className="rounded-xl border-black/[0.1] h-11"
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="offer-lease-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="offer-lease-cancel">
            Cancel
          </Button>
          <Button
            data-testid="offer-lease-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold"
          >
            {submitting ? "Creating..." : "Send Lease Offer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
